import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { api } from "../common/api";

const DatabaseTables: React.FC = () => {
  const [tables, setTables] = useState<string[]>([]);
  const [open, setOpen] = useState(false); // accordion state
  const [loading, setLoading] = useState(true);

  const activeConnection = JSON.parse(
    localStorage.getItem("activeConnection") || "{}"
  );
  const token = JSON.parse(localStorage.getItem("token") || '""');
  const accessToken = token.accessToken;
  const activeConnectionId = activeConnection.id;
  const fetchedConnectionId = useRef<string | null>(null);

  if (!activeConnectionId) {
    toast.error(
      "No active connection selected. Please select a connection to view database tables."
    );
    return null;
  }

  api.setAuthToken(accessToken);

  useEffect(() => {
    console.log("Fetching schema for connection:", activeConnectionId);
    console.log("User token:", token);

    if (fetchedConnectionId.current === activeConnectionId) return;
    fetchedConnectionId.current = activeConnectionId;
    setLoading(true);
    api
      .extractSchema({ connection_id: activeConnectionId })
      .then((response: any) => {
        console.log("Schema extracted successfully:", response);

        // Handle both direct table_names array and tables object array
        if (response?.table_names && Array.isArray(response.table_names)) {
          setTables(response.table_names);
          // toast.success(
          //   `Database schema extracted: ${response.table_names.length} tables found.`
          // );
        } else if (response?.tables && Array.isArray(response.tables)) {
          // Extract table names from tables array
          const tableNames = response.tables.map((t: any) => t.table_name || t);
          setTables(tableNames);
          // toast.success(
          //   `Database schema extracted: ${tableNames.length} tables found.`
          // );
        } else {
          console.warn("Unexpected response format:", response);
          setTables([]);
          toast.warning("Schema retrieved but no tables found.");
        }

        setLoading(false);
      })
      .catch((error: any) => {
        console.error("Error extracting schema:", error);
        console.error("Error details:", {
          status: error.status,
          message: error.message,
          data: error.data,
        });

        setLoading(false);

        if (error.status === 404) {
          toast.error(
            "No schema found. Please update vectors for this connection first."
          );
        } else if (error.status === 401) {
          //   toast.error("Unauthorized. Please log in again.");
        } else {
          toast.error(error.message || "Failed to extract database schema.");
        }
      });
  }, [activeConnectionId]);

  return (
    <div className="bg-gray-800 text-white rounded-lg p-6 w-full mx-auto shadow-lg">
      {/* Accordion Container */}
      <div className="border border-gray-700 rounded-lg overflow-hidden">
        {/* Accordion Trigger */}
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex justify-between items-center p-4 bg-gray-900 hover:bg-gray-700 transition text-lg font-semibold"
        >
          <span>
            Database Tables
            {!loading && tables.length > 0 && ` (${tables.length})`}
          </span>

          {/* Chevron Icon */}
          <svg
            className={`w-5 h-5 transform transition-transform ${
              open ? "rotate-180" : "rotate-0"
            }`}
            fill="none"
            stroke="white"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Accordion Content */}
        <div
          className={`
            overflow-hidden transition-all duration-300 
            ${open ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"}
          `}
        >
          <div className="p-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                <span className="ml-3 text-gray-400">Loading tables...</span>
              </div>
            ) : tables.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-400 mb-2">No tables found.</p>
                <p className="text-sm text-gray-500">
                  Please run the update vector function to extract your database
                  schema.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                {tables.map((table, index) => (
                  <div
                    key={index}
                    className="
                      bg-gray-800 
                      text-white 
                      py-3 px-4
                      rounded-xl 
                      shadow 
                      cursor-pointer 
                      hover:bg-gray-700 
                      transition 
                      border border-gray-700
                      inline-block
                    "
                    style={{
                      width: "fit-content",
                      minWidth: "120px",
                    }}
                    title={`Click to view ${table} details`}
                  >
                    <span className="font-medium">{table}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseTables;
